import Typesetter from 'palt-typesetting';

const TARGET_SELECTOR = '.js-typeset-palt, .text';
const EXCLUDE_SELECTOR = [
  '.js-marquee',
  '.fittext',
  '.js-carousel-counter',
  '.js-pickup-carousel-counter',
  '.js-countdown-el',
  '.calendar-container',
  '.selected-date-programs',
  '.calendar-day-number',
  '.no-typeset-palt',
  '[data-no-typeset-palt]',
  'script',
  'style',
  'textarea',
  'code',
  'pre',
  'kbd',
  'samp',
].join(',');
const OBSERVER_CONFIG = {
  childList: true,
  characterData: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['class', 'data-no-typeset-palt'],
};

const options = {
  // 単語区切りでは改行せず、ブラウザ標準の改行に任せる
  useWordBreak: false,
  // 和文と欧文の間に四分アキスペースを挿入する
  insertThinSpaces: true,
  // 英数字を専用の要素で囲む
  wrapLatin: true,
  // 公式デモと同じ特例文字を専用の要素で囲む
  wrapChars: [
    { char: '↗', label: 'arrow' },
    { char: ['(', ')'], label: 'parenthesis' },
  ],
  // 罫線などの連続文字に専用の字間処理は行わない
  noSpaceBetweenNoBreaks: false,
  // 公式デモと同じ文字間のカーニングを適用する
  kerningRules: [
    { between: ['美', 'し'], value: 60 },
    { between: ['ス', 'ト'], value: 120 },
    { between: ['イ', 'ブ'], value: 20 },
    { between: ['ブ', 'ラ'], value: -30 },
    { between: ['ラ', 'リ'], value: 30 },
    { between: ['て', '、'], value: -60 },
    { between: ['す', '。'], value: -120 },
    { between: ['よ', 'う'], value: 60 },
    { between: ['う', 'な'], value: 40 },
    { between: ['さ', 'れ'], value: 20 },
    { between: ['れ', 'た'], value: -60 },
    { between: ['供', 'し'], value: 40 },
    { between: ['し', 'ま'], value: 70 },
  ],
};

// 開きクオート直後に挿入された四分アキだけを削除する
const OPENING_QUOTE_RE = /^[“”"‘’']+$/;

const removeThinSpaceAfterOpeningQuotes = (targets) => {
  targets.forEach((el) => {
    el.querySelectorAll('.typesetting-latin').forEach((span) => {
      if (!OPENING_QUOTE_RE.test(span.textContent)) return;

      const next = span.nextElementSibling;
      if (next && next.classList.contains('typesetting-thin-space')) next.remove();
    });
  });
};

let observer = null;
let observedRoot = null;
let scheduledRender = 0;
let pendingTargets = new Set();

const isElement = (node) => node && node.nodeType === 1;

const getElement = (node) => {
  if (!node) return null;
  if (isElement(node)) return node;
  return node.parentElement || null;
};

const isConnected = (el) => {
  if (!el) return false;
  if (typeof el.isConnected === 'boolean') return el.isConnected;
  return document.documentElement.contains(el);
};

const getTopTarget = (el) => {
  if (!el || !el.matches || !el.matches(TARGET_SELECTOR)) return null;

  let target = el;
  let parentTarget = target.parentElement && target.parentElement.closest(TARGET_SELECTOR);

  while (parentTarget) {
    target = parentTarget;
    parentTarget = target.parentElement && target.parentElement.closest(TARGET_SELECTOR);
  }

  return target;
};

const getTargetFromNode = (node) => {
  const el = getElement(node);
  if (!el) return null;

  const target = el.matches(TARGET_SELECTOR) ? el : el.closest(TARGET_SELECTOR);
  return getTopTarget(target);
};

const filterTargets = (targets, force = false) => {
  const filtered = [];

  targets.forEach((el) => {
    const target = getTopTarget(el);

    if (!target) return;
    if (filtered.indexOf(target) !== -1) return;
    if (!force && target.dataset.typesetPalt === 'done') return;
    if (!target.textContent || !target.textContent.trim()) return;
    if (target.closest(EXCLUDE_SELECTOR)) return;

    filtered.push(target);
  });

  return filtered;
};

const getTargets = (root, force = false) => {
  const scope = root && root.nodeType === 1 ? root : document;
  const targets = [];

  if (scope.matches && scope.matches(TARGET_SELECTOR)) targets.push(scope);
  targets.push(...scope.querySelectorAll(TARGET_SELECTOR));

  return filterTargets(targets, force);
};

const pauseObserver = () => {
  if (!observer) return;
  observer.takeRecords();
  observer.disconnect();
};

const resumeObserver = () => {
  if (!observer || !observedRoot || !isConnected(observedRoot)) return;
  observer.takeRecords();
  observer.observe(observedRoot, OBSERVER_CONFIG);
};

const renderTargets = (targets, force = false) => {
  const filtered = filterTargets(targets, force);
  if (!filtered.length) return;

  pauseObserver();

  try {
    const typesetter = new Typesetter(options);
    typesetter.renderToElements(filtered);
    removeThinSpaceAfterOpeningQuotes(filtered);

    filtered.forEach((el) => {
      el.dataset.typesetPalt = 'done';
    });
  } finally {
    resumeObserver();
  }
};

const flushPendingTargets = () => {
  scheduledRender = 0;

  const targets = Array.from(pendingTargets).filter(isConnected);
  pendingTargets = new Set();

  targets.forEach((el) => {
    if (el.dataset) delete el.dataset.typesetPalt;
  });

  renderTargets(targets, true);
};

const requestRender = () => {
  if (scheduledRender) return;

  if (window.requestAnimationFrame) {
    scheduledRender = window.requestAnimationFrame(flushPendingTargets);
  } else {
    scheduledRender = window.setTimeout(flushPendingTargets, 16);
  }
};

const queueTarget = (target) => {
  if (!target || !isConnected(target) || target.closest(EXCLUDE_SELECTOR)) return;

  pendingTargets.add(target);
  requestRender();
};

const collectTargetsFromNode = (node, targets) => {
  const el = getElement(node);
  if (!el) return;

  const target = getTargetFromNode(el);
  if (target) {
    targets.add(target);
    return;
  }

  if (isElement(el)) {
    getTargets(el, true).forEach((item) => targets.add(item));
  }
};

const handleMutations = (mutations) => {
  const targets = new Set();

  mutations.forEach((mutation) => {
    if (mutation.type === 'attributes') {
      const el = getElement(mutation.target);
      if (!el) return;

      if (el.matches(TARGET_SELECTOR)) {
        collectTargetsFromNode(el, targets);
        return;
      }

      if (!el.closest(TARGET_SELECTOR)) {
        getTargets(el, true).forEach((item) => targets.add(item));
      }

      return;
    }

    collectTargetsFromNode(mutation.target, targets);

    if (mutation.type === 'childList') {
      mutation.addedNodes.forEach((node) => {
        collectTargetsFromNode(node, targets);
      });
    }
  });

  targets.forEach(queueTarget);
};

export function paltTypesetting(root = document, { force = false } = {}) {
  renderTargets(getTargets(root, force), force);
}

export function observePaltTypesetting(root = document) {
  if (typeof MutationObserver === 'undefined') return null;

  const rootEl = root === document ? document.body || document.documentElement : root;
  if (!rootEl) return null;

  if (observer) observer.disconnect();

  observedRoot = rootEl;
  observer = new MutationObserver(handleMutations);
  observer.observe(observedRoot, OBSERVER_CONFIG);

  return observer;
}
